import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Trash2, ArrowLeft } from 'lucide-react';

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, decreaseQuantity, removeFromCart, addToCart } = useCart();

  // 🌟 BULLETPROOF LOGIC: Target explicit product IDs directly from the event payload
  const handleStepUp = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(item, item.size);
  };

  const handleStepDown = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    // FIXED: Changed 'currentId' to 'item._id' so it reads properly within scope
    decreaseQuantity(item._id, item.size);
  };

  const totalItemsCount = cart ? cart.reduce((total, item) => total + (item.quantity || 1), 0) : 0;

  const cartTotalAmount = cart ? cart.reduce((total, item) => {
    const activePrice = item.discountPrice || item.price || 0;
    return total + (activePrice * (item.quantity || 1));
  }, 0) : 0;

  return (
    <div className="min-h-screen bg-[#F5F5DC] pt-32 pb-24 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-[1400px] mx-auto">
        
        <button 
          onClick={() => navigate('/')}
          className="flex items-center gap-2 bg-[#2C1E12] text-[#F5F5DC] text-[9px] font-black uppercase tracking-[0.25em] px-5 py-2.5 rounded-sm mb-10 hover:opacity-90 transition-all duration-300 shadow-sm"
        >
          <ArrowLeft size={12} strokeWidth={2.5} />
          Back to Store
        </button>

        <div className="mb-10 border-b border-[#2C1E12]/10 pb-4">
          <h1 className="text-3xl md:text-4xl font-black text-[#2C1E12] uppercase tracking-tight">
            Shopping Bag ({totalItemsCount})
          </h1>
        </div>

        {!cart || cart.length === 0 ? (
          <div className="text-center py-24 bg-[#E5D3B3]/20 border border-[#2C1E12]/5 rounded-sm p-8">
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#664229] font-bold mb-8 opacity-70">
              Your lookbook collection bag is currently empty.
            </p>
            <button onClick={() => navigate('/')} className="border border-[#2C1E12] text-[#2C1E12] px-12 py-4 text-[10px] uppercase tracking-[0.3em] font-bold">
              Discover New Arrivals
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
            
            <div className="lg:col-span-2 space-y-6">
              {cart.map((item) => {
                const activePrice = item.discountPrice || item.price || 0;
                const itemId = item._id || item.id;
                
                return (
                  <div key={`${itemId}-${item.size}`} className="flex flex-col sm:flex-row items-center gap-6 bg-[#E5D3B3]/30 p-5 rounded-sm border border-[#2C1E12]/5 relative group">
                    
                    <div className="w-28 h-36 bg-[#1A1512]/5 rounded-sm overflow-hidden flex-shrink-0 border border-[#2C1E12]/10">
                      <img src={item.images?.[0] || item.imageUrl || '/placeholder.jpg'} alt={item.name} className="w-full h-full object-cover" />
                    </div>

                    <div className="flex-1 min-w-0 text-center sm:text-left space-y-1">
                      <h3 className="font-bold text-sm text-[#2C1E12] uppercase truncate max-w-md">{item.name}</h3>
                      <div className="text-[10px] uppercase tracking-widest text-[#664229]/80 font-semibold">
                        <p>Size: <span className="text-[#2C1E12] font-black">{item.size || 'N/A'}</span></p>
                        <p>Quantity: <span className="text-[#2C1E12] font-black">{item.quantity || 1}</span></p>
                      </div>
                      <div className="pt-4 text-sm font-black text-[#2C1E12]">₹{activePrice}</div>
                    </div>

                    <div className="flex sm:flex-col items-center gap-4 sm:gap-8 justify-end w-full sm:w-auto">
                      <div className="flex items-center border border-[#2C1E12]/20 rounded-sm bg-[#F5F5DC] overflow-hidden shadow-sm">
                        <button 
                          onClick={(e) => handleStepDown(e, item)}
                          className="px-3 py-1.5 text-xs text-[#2C1E12] hover:bg-[#2C1E12]/5 font-mono select-none font-bold"
                        >
                          -
                        </button>
                        <span className="px-3 text-xs font-mono font-black text-[#2C1E12] min-w-[24px] text-center select-none">
                          {item.quantity || 1}
                        </span>
                        <button 
                          onClick={(e) => handleStepUp(e, item)}
                          className="px-3 py-1.5 text-xs text-[#2C1E12] hover:bg-[#2C1E12]/5 font-mono select-none font-bold"
                        >
                          +
                        </button>
                      </div>

                      <button onClick={() => removeFromCart && removeFromCart(itemId, item.size)} className="text-[#664229]/60 hover:text-red-800 sm:absolute sm:top-5 sm:right-5">
                        <Trash2 size={15} />
                      </button>
                    </div>

                  </div>
                );
              })}
            </div>

            <div className="bg-[#E5D3B3] p-8 rounded-sm shadow-xl border border-[#2C1E12]/10 sticky top-36">
              <h2 className="text-xs font-black uppercase tracking-[0.25em] text-[#2C1E12] border-b border-[#2C1E12]/10 pb-4 mb-6">Order Summary</h2>
              <div className="space-y-4 border-b border-[#2C1E12]/10 pb-6 text-xs font-medium text-[#664229]">
                <div className="flex justify-between">
                  <span>Subtotal Value</span>
                  <span className="font-mono font-bold text-[#2C1E12]">₹{cartTotalAmount}</span>
                </div>
              </div>
              <div className="pt-6 flex justify-between items-baseline mb-8">
                <span className="text-xs font-black uppercase tracking-wider text-[#2C1E12]">Estimated Total</span>
                <span className="text-2xl font-black text-[#2C1E12]">₹{cartTotalAmount}</span>
              </div>
              <button onClick={() => navigate('/checkout')} className="w-full bg-[#2C1E12] text-[#F5F5DC] text-[11px] font-black uppercase tracking-[0.3em] py-5 rounded-sm shadow-md">
                Proceed to Payment
              </button>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;